import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, List, StatusTip } from "tea-component";
import classnames from 'classnames';
import './index.scss';
import SvgIcon from '@home/components/SvgIcon';
import { useIntl, history } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { FRAME_TYPE_MAP } from '@/pages/benchmark/_config/constant';
import { getRecommendGenreList } from '@/pages/benchmark/services/benchmark';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { formatObjUrl, getLogTypeId } from '@/pages/benchmark/utils/utils';
import TextBubbles from '@home/components/TextBubbles';
import { IQuestionList } from './type';
import ACard from '@/pages/dashboard/components/ACard';

const BenchmarkGenreList = React.memo((props: IQuestionList) => {
  const { locale, formatMessage } = useIntl(); // 中英切换方法
  const isEN = locale === LANGUAGE_EN;
  const { title = formatMessage({ id: 'dbb_explore_recommended_genre' }), className, style = {}, onClickItem, onRefreshClick, frameType} = props;
  const [list, setList] = useState([]);
  const [range, setRange] = useState([0, 4]);
  const [currentPage, setCurrentPage] = useState(0);
  const current = useMemo(()=> list.slice(range[0], range[1]),[range, list]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const onClickRefresh = () => {
    setRange(([first, second]) => {
      if(first + 4 >= list?.length) {
        return [0, 4];
      }
      return [first + 4, second + 4];
    });
    setCurrentPage(prev=> prev+ 1);
    buriedBtnPoint({
      buttonId: `800101003`,
      extInfo: JSON.stringify({
        clicked: currentPage + 1
      }),
      pageId: '800101',
      uidType: '', // 基准打点不包含uidType
      typeId: getLogTypeId(frameType),
    });

    onRefreshClick?.();
  };

  const fetchQuestion = useCallback(async () => {
    setList([]);
    setIsLoading(true);
    setIsError(false);
    const payload = { 
      frame_type: FRAME_TYPE_MAP[frameType || 'pcconsole'],
      page_size: 100,
    };
    try {
      const res = await getRecommendGenreList(payload);
      const { data } = res;
      setList(data?.list || []);
      setCurrentPage(0);
      setRange([0, 4]);
    } catch (e) {
      console.log('err', e);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  },[frameType]);

  useEffect(() => {
    fetchQuestion();
  },[fetchQuestion]);

  return (
    <>
      <div className={classnames('dbb-recommend-genre', className)} style={style}>
        <div className="dbb-recommend-genre-row">
          <span className={'dbb-recommend-genre-title'} >{title}</span>
          {
            list?.length > 4 && (
              <SvgIcon type={'shuaxin'} className={'dbb-recommend-genre-refresh-icon'} onClick={()=> {
                onClickRefresh();
              }}/>
            )
          }
        </div>
        {
          isLoading && (
            <div className='dbb-recommend-genre-loading'>
              <StatusTip
                status='loading'
                loadingText={formatMessage({ id: 'common_loading'})}
              />
            </div>
          )
        }
        {
          isError && (
            <div className='dbb-recommend-genre-loading dbb-recommend-genre-error'>
              <Icon type='error' className='error-icon'/>
              {formatMessage({ id: 'common_loading_failed'})}
            </div>
          )
        }
        {
          !isLoading && !isError && (
            <List className={classnames('dbb-recommend-genre-genre-list')}>
              {
                current?.map((val: { code:string; name_ch: string; name_en: string; category: string;},index: number)=> {
                  const name = isEN ? val?.name_en : val?.name_ch;
                  const objUrl={[val?.category]: val?.code};
                  return (
                    <ACard key={val?.code + index} url={`/benchmark/${frameType}/genre?${formatObjUrl(objUrl)}`} isStopPropagation={false}>
                      <List.Item  onClick={()=> {
                        history.push(`/benchmark/${frameType}/genre?${formatObjUrl(objUrl)}`);
                        onClickItem?.(val);
                      }}>
                        <TextBubbles text={name} rowNum={2} className="question"/>
                        {
                          index + range[0] < 1 && (
                            <SvgIcon type="remen" className="flame-icon" />
                          )
                        }
                      </List.Item>
                    </ACard>
                  );
                })
              }
            </List>
          )
        }
      </div>
    </>
  );
});

export default BenchmarkGenreList;